/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-16 09:51:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { RangeSlider, FormLayout, Icon, TextStyle, Link } from '@shopify/polaris';
import { Collapse } from 'react-collapse';
import { CaretDownMinor, ExternalMinor } from '@shopify/polaris-icons';

import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import TextForm from './text-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { MIN_THRESHOLD, MAX_THRESHOLD } from 'src/constants/theme-settings';
import { nonXSSv2 } from 'src/helpers/form-validation-helper';
import { checkHasXss } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  prefix: string,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();

class InventoryStatusForm extends Component<ComposedProps> {
  state = {
    isOpenStyling: true
  };

  componentWillUnmount() {
    const { formValues, prefix, updateField } = this.props;
    const inStockText = _.get(formValues, `${prefix}.inStockText`, '');
    const lowStockText = _.get(formValues, `${prefix}.lowStockText`, '');
    const soldOutText = _.get(formValues, `${prefix}.soldOutText`, '');

    if (checkHasXss(inStockText)) {
      updateField(`${prefix}.inStockText`, '');
    }
    if (checkHasXss(lowStockText)) {
      updateField(`${prefix}.lowStockText`, '');
    }
    if (checkHasXss(soldOutText)) {
      updateField(`${prefix}.soldOutText`, '');
    }
  }

  handleToggleCollapse = name => {
    this.setState({
      [name]: !this.state[name]
    });
  };

  getLowStockText = () => {
    const { i18n, prefix } = this.props;

    return (
      `${prefix}.lowStockText`,
      `${i18n.translate(
        'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_1'
      )} {{${i18n.translate(
        'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_2'
      )}}} ${i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_3')}`
    );
  };

  render() {
    const { i18n, prefix, formValues, labelTranslation, updateField } = this.props;
    const { isOpenStyling } = this.state;

    const lowInventoryThreshold = _.get(formValues, `${prefix}.lowInventoryThreshold`, 0);

    const inStockColor = _.get(formValues, `${prefix}.inStockColor`, '');
    const lowStockColor = _.get(formValues, `${prefix}.lowStockColor`, '');
    const soldOutColor = _.get(formValues, `${prefix}.soldOutColor`, '');
    const colorPickerList = [
      {
        colorValue: inStockColor,
        fieldName: `${prefix}.inStockColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_InStockColor_Item')
      },
      {
        colorValue: lowStockColor,
        fieldName: `${prefix}.lowStockColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Item')
      },
      {
        colorValue: soldOutColor,
        fieldName: `${prefix}.soldOutColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_SoldOutColor_Item')
      }
    ];

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['InventoryStatusThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          {/* Low inventory threshold */}
          <div className="form-section__item">
            <p style={{ marginBottom: '8px' }}>
              {i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_LowThreshHold_Item')}
            </p>
            <RangeSlider
              value={lowInventoryThreshold}
              onChange={value => updateField(`${prefix}.lowInventoryThreshold`, value)}
              min={MIN_THRESHOLD}
              max={MAX_THRESHOLD}
              suffix={<p style={{}}>{lowInventoryThreshold}</p>}
            />
          </div>

          <div className="form-section__item form-section__item--medium-spacing">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_LowThreshHold_Note')}
            </TextStyle>
          </div>
          {/* In stock text */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.inStockText`}
              component={RenderTextField}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_InStockText_Item')}
              placeholder={
                labelTranslation?.inventoryInStockV2?.value ||
                i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_InStockColor_Filled')
              }
              validate={[nonXSSValue]}
            />
          </div>
          {/* Low stock text */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.lowStockText`}
              component={RenderTextField}
              label={i18n.translate(
                'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockText_Item'
              )}
              placeholder={labelTranslation?.inventoryLowStockV2?.value || this.getLowStockText()}
              validate={[nonXSSValue]}
            />
          </div>
          {/* Sold out text */}
          <div className="form-section__item form-section__item--medium-spacing">
            <Field
              name={`${prefix}.soldOutText`}
              component={RenderTextField}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_SoldOutText_Item')}
              placeholder={
                labelTranslation?.inventorySoldOutV2?.value ||
                i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_SoldOutColor_Filled')
              }
              validate={[nonXSSValue]}
            />
          </div>
        </div>

        <div className="form-section form-section--bordered" style={{ marginTop: '20px' }}>
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenStyling')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_Styling_Title')}
            </span>
            <span className={`caret ${!isOpenStyling ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenStyling}>
            <div className="form-section__item">
              <p className="title title--small">
                {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
              </p>
              {colorPickerList.map((item, index) => (
                <RenderColorPickerItem
                  key={index}
                  colorValue={item.colorValue}
                  fieldName={item.fieldName}
                  fieldLabel={item.fieldLabel}
                  updateField={updateField}
                />
              ))}
            </div>
            <TextForm
              formValues={formValues}
              updateField={updateField}
              prefix={prefix}
              showTextTransform
            />
          </Collapse>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(InventoryStatusForm);
